import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchVendorPoEmailDrafts,
  fetchVendorPoVendorSummary,
  poAttachmentLabel,
  previewVendorPurchaseOrders,
  sendVendorPurchaseOrdersBatch,
  PRICES_EXCLUDE_VAT,
  unpricedLineNote,
  vendorPoBatchSendErrorMessage,
  vendorPoSendErrorMessage,
  vendorPoSendProblems,
  vendorPoSendReasonWords,
  type BatchSendResult,
  type VendorPoEmailDraft,
  type VendorPoSendRow,
  type VendorPoSummary,
} from '../lib/vendorPurchaseOrderApi';
import { openPrintInNewTab } from '../lib/serverPrint';
import { formatDate as fmtDate } from "../utils/dateFormat";
import { formatCurrency } from "../utils/priceUtils";
import EmailDraftFields from './EmailDraftFields';

interface VendorBatchSendModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const isAlreadySent = (status: string): boolean => status === 'sent' || status === 'billed';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The vendor-summary response carries no currency, so no label (a currency is
// never invented); rounded to the cent, not ceiled — the owner is
// BUSINESS_RULES#quotation-screen-money-labels.
const formatPoTotal = (value: number | null): string =>
  value === null || value === undefined ? '—' : formatCurrency(value, null, { ceil: false });

const vendorKey = (v: VendorPoSummary): string => v.vendorId || v.vendorName;

// What a PO not yet with the supplier reads as in this picker.
const PO_STATUS_WORD: Record<string, string> = { amended: 'Amended', cancelled: 'Cancelled' };

const VendorBatchSendModal: React.FC<VendorBatchSendModalProps> = ({ isOpen, onClose }) => {
  const [view, setView] = useState<'list' | 'review'>('list');
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<VendorPoSummary[]>([]);

  const [selectedVendor, setSelectedVendor] = useState<VendorPoSummary | null>(null);
  const [format, setFormat] = useState<'pdf' | 'excel'>('pdf');
  const [recipient, setRecipient] = useState('');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState<BatchSendResult | null>(null);
  // D180: POs whose email went but could not be marked. Never ticked again here, until a reload re-reads them.
  const [emailedNotMarked, setEmailedNotMarked] = useState<Set<string>>(new Set());
  // D180: the email for the ticked POs, as the server drafts it; what these hold is what goes.
  const [draft, setDraft] = useState<VendorPoEmailDraft | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftSkipped, setDraftSkipped] = useState<VendorPoSendRow[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const loadSummary = (): (() => void) => {
    let cancelled = false;
    setLoading(true);
    fetchVendorPoVendorSummary()
      .then((data) => {
        if (cancelled) return;
        setVendors(data || []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        window.alert(e instanceof Error ? e.message : 'Failed to load vendor summary');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (!isOpen) return;
    setView('list');
    setVendors([]);
    setSelectedVendor(null);
    setResult(null);
    return loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const openReview = (v: VendorPoSummary) => {
    const validEmail = v.vendorEmail && v.vendorEmail !== '-' && EMAIL_RE.test(v.vendorEmail) ? v.vendorEmail : '';
    setSelectedVendor(v);
    setRecipient(validEmail);
    setFormat('pdf');
    setCheckedIds(new Set(v.pos.filter((po) => !isAlreadySent(po.status) && po.status !== 'cancelled' && !emailedNotMarked.has(po.id)).map((po) => po.id)));
    setResult(null);
    setView('review');
  };

  const backToList = () => {
    setView('list');
    setSelectedVendor(null);
    setResult(null);
  };

  const toggleChecked = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const checkedIdList = Array.from(checkedIds);
  const anyChecked = checkedIdList.length > 0;
  const recipientValid = EMAIL_RE.test(recipient.trim());
  const willResend = !!selectedVendor && selectedVendor.pos.some((po) => checkedIds.has(po.id) && isAlreadySent(po.status));
  const checkedKey = checkedIdList.join(',');
  const reviewingVendor = selectedVendor ? vendorKey(selectedVendor) : null;

  // The text names the ticked POs, so a change of ticks rewrites it.
  useEffect(() => {
    setDraft(null);
    setDraftError(null);
    setDraftSkipped([]);
    setSubject('');
    setBody('');
    setDraftLoading(false);
    if (!reviewingVendor || !checkedKey) return;
    let stale = false;
    const couldNotPrepare = 'The email could not be prepared, so nothing can be sent yet. Change the ticks or go back and try again.';
    setDraftLoading(true);
    fetchVendorPoEmailDrafts(checkedKey.split(','), willResend)
      .then(({ drafts, skipped }) => {
        if (stale) return;
        setDraftSkipped(skipped);
        if (drafts.length !== 1) {
          setDraftError(couldNotPrepare);
          return;
        }
        setDraft(drafts[0]);
        setSubject(drafts[0].subject);
        setBody(drafts[0].body);
        setRecipient((typed) => typed.trim() || drafts[0].recipients[0] || '');
      })
      .catch((e: unknown) => {
        if (!stale) setDraftError(vendorPoSendErrorMessage(e, couldNotPrepare));
      })
      .finally(() => {
        if (!stale) setDraftLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [reviewingVendor, checkedKey, willResend]);

  const handlePreview = async () => {
    if (!anyChecked || previewing) return;
    setPreviewing(true);
    // D165: the tab opens inside the click; the server print takes a few seconds.
    await openPrintInNewTab(() => previewVendorPurchaseOrders(checkedIdList, format), {
      onBlocked: () => window.alert('Allow pop-ups for this site, then press Preview again.'),
      onError: (e) => window.alert(vendorPoSendErrorMessage(e, 'Failed to generate preview')),
    });
    setPreviewing(false);
  };

  const handleSend = async () => {
    if (!selectedVendor || !draft || !recipientValid || sending) return;
    const trimmedRecipient = recipient.trim();
    if (!window.confirm(`Send ${draft.poIds.length} PO(s) to ${trimmedRecipient}?`)) return;
    setSending(true);
    try {
      const res = await sendVendorPurchaseOrdersBatch({
        emails: [{ poIds: draft.poIds, draftVersion: draft.draftVersion, recipients: [trimmedRecipient], subject, body }],
        format,
        ...(willResend ? { resend: true as const } : {}),
      });
      setResult(res);
      setEmailedNotMarked((prev) => new Set([...Array.from(prev), ...res.notRecorded.map((row) => row.id)]));
      // Only what the server says went reads as sent; nothing stays armed for a second click.
      const sentIds = new Set(res.sent.map((row) => row.id));
      setSelectedVendor((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pos: prev.pos.map((po) =>
            sentIds.has(po.id) ? { ...po, status: 'sent', sentAt: new Date().toISOString() } : po
          ),
        };
      });
      setCheckedIds(new Set());
      loadSummary();
    } catch (e: unknown) {
      window.alert(vendorPoBatchSendErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const listBody = loading ? (
    <div className="flex items-center justify-center py-10 text-sm text-slate-500">Loading vendors…</div>
  ) : vendors.length === 0 ? (
    <div className="flex items-center justify-center py-10 text-sm text-slate-500">No vendors have any POs.</div>
  ) : (
    <div className="flex flex-col gap-2">
      {vendors.map((v) => {
        const key = vendorKey(v);
        return (
          <div
            key={key}
            className="flex items-center justify-between gap-3 p-3 border rounded-md"
            style={{ borderColor: '#D7DBD9' }}
          >
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-slate-900 text-sm font-medium truncate">{v.vendorName}</span>
              <span className="text-xs text-slate-500 truncate">
                {v.vendorEmail && v.vendorEmail !== '-' ? v.vendorEmail : <span className="text-amber-600">&#9888; no email</span>}
              </span>
              <span className="text-xs text-slate-500">
                {v.unsentCount} unsent &middot; {v.sentCount} sent
              </span>
              {/* TECH_DEBT #342 — summed across this vendor's whole group of POs
                  (server: unpricedLineCount), the same words the supplier-facing
                  PDF/Excel/email use, not a screen-local phrasing. */}
              {v.unpricedLineCount > 0 && (
                <span className="text-xs text-amber-700">{unpricedLineNote(v.unpricedLineCount)}</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => openReview(v)}
                className="px-3 py-1.5 bg-primary-500 text-white rounded-md text-xs font-semibold hover:bg-primary-600 whitespace-nowrap"
              >
                Review &amp; Send
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const reviewBody = selectedVendor && (
    <>
      <div className="mb-4">
        <button
          type="button"
          onClick={backToList}
          disabled={sending}
          className="text-xs font-semibold text-primary-500 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          &larr; Back to vendors
        </button>
      </div>

      <h3 className="text-base font-semibold leading-6 text-slate-900 mb-4">
        Send to {selectedVendor.vendorName}
      </h3>

      <div className="mb-4">
        <label
          htmlFor="po-batch-recipient"
          className="block mb-2 text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 500, fontSize: '14px', lineHeight: '22px' }}
        >
          Recipient email <span className="text-red-500">*</span>
        </label>
        <input
          id="po-batch-recipient"
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="vendor@supplier.com"
          className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}
        />
        {recipient.trim() && !recipientValid && (
          <p className="mt-1 text-xs text-red-500">Enter a valid email address.</p>
        )}
      </div>

      <div className="mb-4">
        <label
          className="block mb-2 text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 500, fontSize: '14px', lineHeight: '22px' }}
        >
          Format
        </label>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value as 'pdf' | 'excel')}
          className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}
        >
          <option value="pdf">PDF</option>
          <option value="excel">Excel</option>
        </select>
      </div>

      <div className="mb-4">
        <label
          className="block mb-2 text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 500, fontSize: '14px', lineHeight: '22px' }}
        >
          Purchase orders
        </label>
        <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
          {selectedVendor.pos.map((po) => {
            const isSent = isAlreadySent(po.status);
            return (
              <label
                key={po.id}
                className="flex items-center gap-3 p-2 border rounded-md cursor-pointer"
                style={{ borderColor: '#D7DBD9' }}
              >
                <input
                  type="checkbox"
                  checked={checkedIds.has(po.id)}
                  disabled={sending || emailedNotMarked.has(po.id)}
                  onChange={() => toggleChecked(po.id)}
                />
                <span className="text-sm text-slate-900 flex-1 truncate">
                  {po.poNumber} &middot; {formatPoTotal(po.totalPrice)}
                  {/* D82: the wording the PO page, list and the supplier's PDF use. */}
                  {po.priceBasis === 'before_vat' && (
                    <span className="text-slate-500"> ({PRICES_EXCLUDE_VAT})</span>
                  )}
                  {/* TECH_DEBT #342 — same wording as the PDF/Excel/email this PO
                      is about to be sent as. */}
                  {po.unpricedLineCount > 0 && (
                    <span className="text-amber-700"> ({unpricedLineNote(po.unpricedLineCount)})</span>
                  )}
                  {emailedNotMarked.has(po.id) && (
                    <span className="text-red-600"> (email went, do not send it again)</span>
                  )}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                    isSent ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {isSent && po.sentAt ? `Sent · ${fmtDate(po.sentAt)}` : isSent ? 'Sent' : PO_STATUS_WORD[po.status] ?? 'Draft'}
                </span>
              </label>
            );
          })}
        </div>
        <p className="mt-1.5 text-xs text-slate-500">Changing the ticks rewrites the subject and text below.</p>
        {willResend && (
          <p className="mt-1.5 text-xs text-amber-700">A ticked purchase order was already sent: sending again will resend it.</p>
        )}
        {draftSkipped.map((row) => (
          <p key={row.id} className="mt-1.5 text-xs text-amber-700">
            {`${row.poNumber || 'A purchase order'} will not go: ${vendorPoSendReasonWords(row.reason)}.`}
          </p>
        ))}
      </div>

      {draftLoading && (
        <p role="status" className="mb-4 text-sm text-slate-600">Preparing the email…</p>
      )}
      {draftError && (
        <p role="alert" className="mb-4 text-sm text-red-600">{draftError}</p>
      )}
      <EmailDraftFields
        subject={subject}
        onSubjectChange={setSubject}
        body={body}
        onBodyChange={setBody}
        attachment={draft ? poAttachmentLabel(draft.poNumbers, format) : null}
        disabled={!draft || sending}
      />

      {result && (
        <div className="mb-4 text-xs">
          <p className="text-green-700">Sent {result.sent.length}.</p>
          {vendorPoSendProblems(result).map((problem) => (
            <p key={problem} className="text-amber-700">{problem}</p>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={handlePreview}
          disabled={!anyChecked || previewing}
          className="flex-1 bg-white border border-primary-500 text-primary-500 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors h-10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span style={{ fontFamily: 'Public Sans', fontWeight: 600, fontSize: '14px', lineHeight: '22px' }}>
            {previewing ? 'Preparing…' : 'Preview document'}
          </span>
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={!draft || !recipientValid || sending}
          className="flex-1 bg-primary-500 text-white rounded-lg flex items-center justify-center hover:bg-primary-600 transition-colors h-10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span style={{ fontFamily: 'Public Sans', fontWeight: 600, fontSize: '14px', lineHeight: '22px' }}>
            {sending ? 'Sending…' : willResend ? 'Resend selected' : 'Send selected'}
          </span>
        </button>
      </div>
    </>
  );

  const content = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 modal-overlay-safe">
      <div className="bg-white rounded-lg relative w-full max-w-[610px] max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4">
          <h2 className="text-base font-semibold leading-6 text-slate-900">
            Send by Vendor
          </h2>
          <button
            onClick={onClose}
            disabled={sending}
            className="w-6 h-6 flex items-center justify-center bg-[#D9D9D9] rounded hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <span className="text-slate-400 text-lg leading-none">×</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 pb-4">{view === 'list' ? listBody : reviewBody}</div>

        {/* Footer */}
        {view === 'list' && (
          <div className="p-6 pt-0 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white border border-[#D7DBD9] rounded-lg flex items-center justify-center hover:bg-slate-50 transition-colors h-10"
            >
              <span
                className="text-slate-900"
                style={{ fontFamily: 'Public Sans', fontWeight: 600, fontSize: '16px', lineHeight: '24px' }}
              >
                Close
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default VendorBatchSendModal;
